import { Link, createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { ServicePage } from "@/components/site/ServicePage";
import heroImg from "@/assets/volanteo.jpg";
import heroImgWebp from "@/assets/volanteo.webp";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/volanteo-publicitario";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";

const DEFAULT_TITLE = "Volanteo y Volantes Publicitarios en Lima | Improve";
const DEFAULT_DESCRIPTION =
  "Volantes publicitarios y volanteo en Lima: reparto con rutas planificadas, personal uniformado y reporte de entregas por punto. Pide tu propuesta.";

const props = {
  slug: "/volanteo-publicitario" as const,
  heroImg,
  heroImgWebp,
  heroSrcset:
    "/assets/volanteo-400w.webp 400w, /assets/volanteo-680w.webp 680w, /assets/volanteo-800w.webp 800w",
};
const relatedSlugs = [
  "/activaciones-btl",
  "/sampling-degustaciones",
  "/eventos-corporativos",
] as const;

function VolanteoPublicitarioPage() {
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  const slot = siteImageSlots?.["servicio-volanteo-publicitario"];
  return (
    <PageShell>
      <ServicePage
        {...props}
        vista={vista}
        heroImg={slot?.src ?? props.heroImg}
        heroImgWebp={slot?.src ?? props.heroImgWebp}
        heroSrcset={slot?.srcSet ?? props.heroSrcset}
        relatedSlugs={[...relatedSlugs]}
      />
    </PageShell>
  );
}

export const Route = createFileRoute("/volanteo-publicitario")({
  component: VolanteoPublicitarioPage,
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: ({ context }) => datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const slot = match.context.siteImageSlots?.["servicio-volanteo-publicitario"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/volanteo-publicitario",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/volanteo-publicitario"),
        {
          rel: "preload",
          as: "image",
          href: slot?.src ?? heroImgWebp,
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData,
            "/volanteo-publicitario",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});
